import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import { Calendar, ArrowLeft, PlusCircle, Trash, RefreshCw } from "lucide-react";

const THEME = "rgb(37, 99, 235)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";

/* ─────────────────────────────────────────────────────────────
   CUSTOM SEARCHABLE SELECT COMPONENT
   ───────────────────────────────────────────────────────────── */
function SearchableSelect({
  options = [],       // [{ value, label, sub }]
  value,
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  id,
  showSearch = false, // added to selectively show the search bar
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const optionsRefs = useRef([]);

  const selected = options.find((o) => o.value === value);

  const filtered = (showSearch && search.trim())
    ? options.filter(
      (o) =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        (o.sub && o.sub.toLowerCase().includes(search.toLowerCase()))
    )
    : options;

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* focus handling on open */
  useEffect(() => {
    if (open) {
      const selectedIndex = filtered.findIndex((o) => o.value === value);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);

      if (showSearch && searchRef.current) {
        searchRef.current.focus();
      }
    } else {
      setFocusedIndex(-1);
    }
  }, [open, showSearch, filtered.length, value]);

  /* scroll focused option into view */
  useEffect(() => {
    if (focusedIndex >= 0 && optionsRefs.current[focusedIndex]) {
      optionsRefs.current[focusedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
    setSearch("");
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch("");
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (disabled || loading) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev + 1) % filtered.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filtered.length) {
          handleSelect(filtered[focusedIndex].value);
        } else if (filtered.length > 0) {
          handleSelect(filtered[0].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setSearch("");
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
        break;
      case "Tab":
        setOpen(false);
        setSearch("");
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapRef} onKeyDown={handleKeyDown} style={{ position: "relative", width: "100%", zIndex: open ? 1000 : 1 }} id={id}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className="ado-focusable"
        onClick={handleToggle}
        disabled={disabled || loading}
        style={{
          ...ss.trigger,
          opacity: disabled ? 0.55 : 1,
          cursor: disabled || loading ? "not-allowed" : "pointer",
          borderColor: open ? THEME : "#cbd5e1",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={ss.triggerText}>
          {loading
            ? "Loading…"
            : selected
              ? (
                <span>
                  {selected.label}
                  {selected.sub && (
                    <span style={ss.triggerSub}> · {selected.sub}</span>
                  )}
                </span>
              )
              : <span style={{ color: "#94a3b8" }}>{placeholder}</span>}
        </span>
        <svg
          style={{
            ...ss.chevron,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="5 8 10 13 15 8" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={ss.panel} role="listbox">
          {/* Search bar */}
          {showSearch && (
            <div style={ss.searchWrap}>
              <svg style={ss.searchIcon} viewBox="0 0 20 20" fill="none" stroke="#94a3b8" strokeWidth="2">
                <circle cx="8.5" cy="8.5" r="5.5" />
                <line x1="13" y1="13" x2="17" y2="17" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                style={ss.searchInput}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={ss.clearBtn}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Options list */}
          <div style={ss.optionList}>
            {filtered.length === 0 ? (
              <div style={ss.emptyMsg}>No results found</div>
            ) : (
              filtered.map((o, idx) => {
                const isActive = o.value === value;
                const isFocused = idx === focusedIndex;
                return (
                  <button
                    key={o.value}
                    ref={(el) => (optionsRefs.current[idx] = el)}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    tabIndex={-1}
                    onClick={() => handleSelect(o.value)}
                    onMouseEnter={() => setFocusedIndex(idx)}
                    style={{
                      ...ss.option,
                      background: isActive ? THEME_LIGHT : "transparent",
                      outline: isFocused ? `1px solid ${THEME}` : "none",
                      outlineOffset: "-1px",
                      color: (isActive || isFocused) ? THEME : "#0f172a",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    <span style={ss.optionLabel}>{o.label}</span>
                    {o.sub && <span style={ss.optionSub}>{o.sub}</span>}
                    {isActive && (
                      <svg style={ss.checkIcon} viewBox="0 0 20 20" fill={THEME}>
                        <path d="M5 10l4 4 6-8" stroke={THEME} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   CUSTOM DATE PICKER COMPONENT (Weekends Disabled)
   ───────────────────────────────────────────────────────────── */
function CustomDatePicker({
  value, // "YYYY-MM-DD"
  onChange, // function(val)
  placeholder = "Select date…",
  disabled = false,
  id,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Parse current date or default to today's month/year for navigation
  const today = new Date();
  const initialDate = value ? new Date(value) : today;
  const [navDate, setNavDate] = useState(initialDate);

  // Sync navDate when value changes externally
  useEffect(() => {
    if (value) {
      setNavDate(new Date(value));
    }
  }, [value]);

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const selectDate = (date) => {
    // Format to YYYY-MM-DD
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    onChange(`${y}-${m}-${d}`);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  const handleToday = () => {
    const t = new Date();
    // Only allow selecting today if it's not a weekend
    if (t.getDay() !== 0 && t.getDay() !== 6) {
      selectDate(t);
    } else {
      setNavDate(t);
    }
  };

  // Nav actions
  const prevMonth = () => {
    setNavDate(new Date(navDate.getFullYear(), navDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setNavDate(new Date(navDate.getFullYear(), navDate.getMonth() + 1, 1));
  };

  // Generate calendar days
  const year = navDate.getFullYear();
  const month = navDate.getMonth(); // 0-indexed

  // First day of current month (0 = Sunday, 1 = Monday, etc.)
  const firstDayIndex = new Date(year, month, 1).getDay();
  // Total days in current month
  const totalDays = new Date(year, month + 1, 0).getDate();
  // Total days in previous month
  const prevTotalDays = new Date(year, month, 0).getDate();

  const daysGrid = [];

  // Previous month days to fill index gap
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    daysGrid.push({
      date: new Date(year, month - 1, prevTotalDays - i),
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let i = 1; i <= totalDays; i++) {
    daysGrid.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Next month days to fill to 42 items (6 rows)
  const remaining = 42 - daysGrid.length;
  for (let i = 1; i <= remaining; i++) {
    daysGrid.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  // Format selected value for display
  const formatDateDisplay = (val) => {
    if (!val) return "";
    const [y, m, d] = val.split("-");
    const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
    if (isNaN(dateObj.getTime())) return val;
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const selectedStr = value ? value : "";
  const displayVal = formatDateDisplay(selectedStr);

  return (
    <div
      ref={wrapRef}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
      style={{ position: "relative", width: "100%", zIndex: open ? 1000 : 1 }}
      id={id}
    >
      {/* Trigger */}
      <button
        type="button"
        className="ado-focusable"
        onClick={handleToggle}
        disabled={disabled}
        style={{
          ...dp.trigger,
          opacity: disabled ? 0.55 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          borderColor: open ? THEME : "#cbd5e1",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
        }}
      >
        <span style={ss.triggerText}>
          {displayVal ? (
            <span>{displayVal}</span>
          ) : (
            <span style={{ color: "#94a3b8" }}>{placeholder}</span>
          )}
        </span>
        {/* Calendar Icon */}
        <svg
          style={{ width: 18, height: 18, flexShrink: 0, color: "#64748b" }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {/* Popover */}
      {open && (
        <div style={{
          ...ss.panel,
          padding: "10px",
          width: "250px",
          boxSizing: "border-box",
          top: "calc(100% + 4px)"
        }}>
          {/* Header */}
          <div style={dp.header}>
            <button
              type="button"
              onClick={prevMonth}
              style={dp.navBtn}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <polyline points="12 5 7 10 12 15" />
              </svg>
            </button>
            <span style={dp.navTitle}>
              {monthNames[month]} {year}
            </span>
            <button type="button" onClick={nextMonth} style={dp.navBtn}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <polyline points="8 5 13 10 8 15" />
              </svg>
            </button>
          </div>

          {/* Weekday headers */}
          <div style={dp.weekdaysGrid}>
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <span key={day} style={dp.weekday}>
                {day}
              </span>
            ))}
          </div>

          {/* Days Grid */}
          <div style={dp.daysGrid}>
            {daysGrid.map(({ date, isCurrentMonth }, idx) => {
              const dateY = date.getFullYear();
              const dateM = String(date.getMonth() + 1).padStart(2, "0");
              const dateD = String(date.getDate()).padStart(2, "0");
              const dateStr = `${dateY}-${dateM}-${dateD}`;
              const isSelected = dateStr === selectedStr;

              const isToday =
                date.getDate() === today.getDate() &&
                date.getMonth() === today.getMonth() &&
                date.getFullYear() === today.getFullYear();

              const dayOfWeek = date.getDay();
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => selectDate(date)}
                  disabled={isWeekend}
                  style={{
                    ...dp.dayCell,
                    color: isSelected
                      ? "#ffffff"
                      : isWeekend
                        ? "#cbd5e1"
                        : isCurrentMonth
                          ? "#0f172a"
                          : "#cbd5e1",
                    background: isSelected
                      ? THEME
                      : isToday
                        ? THEME_LIGHT
                        : "transparent",
                    fontWeight: isSelected || isToday ? "600" : "400",
                    border: isToday && !isSelected ? `1px solid ${THEME}` : "none",
                    cursor: isWeekend ? "not-allowed" : "pointer",
                    opacity: isWeekend ? 0.4 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !isWeekend) {
                      e.currentTarget.style.background = "#f1f5f9";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && !isWeekend) {
                      e.currentTarget.style.background = isToday ? THEME_LIGHT : "transparent";
                    }
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer actions */}
          <div style={dp.footer}>
            <button type="button" onClick={handleClear} style={dp.footerBtn}>
              Clear
            </button>
            <button type="button" onClick={handleToday} style={{ ...dp.footerBtn, color: THEME, fontWeight: "600" }}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   HOLIDAYS DASHBOARD COMPONENT
   ───────────────────────────────────────────────────────────── */
export default function Holidays({ onBack }) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  /* ── DATA STATES ── */
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  /* ── FORM STATES ── */
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayName, setHolidayName] = useState("");

  /* ── UI STATES ── */
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* =====================================================
     LOAD HOLIDAYS
  ===================================================== */
  const loadHolidays = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/assignments/holidays?year=${selectedYear}&_t=${Date.now()}`
      );
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : [];
      setHolidays(list.filter((h) => !h.isDeleted && h.isActive !== false && h.is_active !== 0));
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to load holidays.");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, instance, accounts]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  /* =====================================================
     SUBMIT HOLIDAY
  ===================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!holidayDate || !holidayName.trim()) {
      setError("Please select a holiday date and enter a holiday name.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      await callApi(instance, accounts, "POST", "/api/assignments/holidays", {
        holidayDate,
        holidayName: holidayName.trim(),
      });

      setSuccess("Holiday added successfully.");
      setHolidayDate("");
      setHolidayName("");

      // Auto refresh list
      loadHolidays();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to add holiday.");
    } finally {
      setSubmitting(false);
    }
  };

  /* =====================================================
     DELETE HOLIDAY
  ===================================================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this holiday?")) return;

    try {
      setError("");
      setSuccess("");

      await callApi(instance, accounts, "DELETE", `/api/assignments/holidays/${id}`);

      setSuccess("Holiday deleted successfully.");
      loadHolidays();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to delete holiday.");
    }
  };

  // Generate a list of years starting from current year onwards (e.g., current year and next 4 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

  return (
    <div style={s.page} id="ado-holidays-container">
      <style>{`
        #ado-holidays-container input:focus,
        #ado-holidays-container select:focus,
        #ado-holidays-container button:focus {
          border-color: ${THEME} !important;
          box-shadow: 0 0 0 3px ${THEME_LIGHT} !important;
          outline: none !important;
        }
        .ado-delete-btn {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 6px;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
          outline: none;
        }
        .ado-delete-btn:hover {
          background-color: #ef4444 !important;
          color: #ffffff !important;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.breadcrumb}>
          <span>Administration</span> &gt;{" "}
          <span
            onClick={onBack || (() => navigate("/manage"))}
            style={{ color: "#2563eb", fontWeight: "600", cursor: "pointer" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            title="Click to go to Manage Menu"
          >
            Manage
          </span>{" "}
          &gt; <span style={{ color: "#0f172a", fontWeight: "600" }}>Holidays</span>
        </div>
        <h1 style={s.title}>Holidays</h1>
        <p style={s.sub}>Manage and view organization holidays for the year.</p>
      </div>

      {/* ALERTS */}
      {error && <div style={s.errorBox}>{error}</div>}
      {success && <div style={s.successBox}>{success}</div>}

      {/* GRID LAYOUT */}
      <div style={s.gridContainer}>
        {/* LEFT COLUMN: Add Form */}
        <div style={s.formColumn}>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Add New Holiday</h3>
            <form onSubmit={handleSubmit} style={s.form}>
              <div style={s.field}>
                <label style={s.label}>Holiday Date</label>
                <CustomDatePicker
                  value={holidayDate}
                  onChange={(val) => setHolidayDate(val)}
                  placeholder="Select holiday date…"
                />
              </div>

              <div style={s.field}>
                <label style={s.label}>Holiday Name</label>
                <input
                  type="text"
                  placeholder="e.g. New Year's Day"
                  style={s.input}
                  value={holidayName}
                  onChange={(e) => setHolidayName(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  ...s.submitBtn,
                  opacity: submitting ? 0.7 : 1,
                  cursor: submitting ? "not-allowed" : "pointer"
                }}
              >
                <PlusCircle size={15} style={{ marginRight: 6 }} />
                {submitting ? "Adding…" : "Add Holiday"}
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT COLUMN: Table */}
        <div style={s.tableColumn}>
          <div style={s.card}>
            <div style={s.tableHeaderRow}>
              <h3 style={s.cardTitle}>Holiday Calendar</h3>
              <div style={s.yearSelector}>
                <label style={{ ...s.label, margin: "0 8px 0 0" }}>Year:</label>
                <div style={{ width: "115px" }}>
                  <SearchableSelect
                    options={years.map((y) => ({ value: y, label: String(y) }))}
                    value={selectedYear}
                    onChange={(val) => setSelectedYear(val)}
                    placeholder="Select Year…"
                    showSearch={false}
                  />
                </div>
                <button style={s.refreshBtn} onClick={loadHolidays} title="Refresh list" disabled={loading}>
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
            </div>

            {holidays.length === 0 && loading ? (
              <div style={s.tableContainer}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Holiday Name</th>
                      <th style={s.thActions}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <tr key={`hol-skel-${i}`} style={s.tr}>
                        <td style={s.td}><div style={{ ...s.skeletonBar, width: "100px" }} /></td>
                        <td style={s.td}><div style={{ ...s.skeletonBar, width: "180px" }} /></td>
                        <td style={s.tdActions}><div style={{ ...s.skeletonBar, width: "24px", margin: "0 auto" }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : holidays.length === 0 && !loading ? (
              <div style={s.emptyBox}>No holidays registered for {selectedYear}.</div>
            ) : (
              <div style={{ ...s.tableContainer, position: "relative" }}>
                {loading && (
                  <div style={s.overlayLoader}>
                    <RefreshCw size={20} color={THEME} style={{ animation: "spin 0.8s linear infinite" }} />
                    <span>Updating holidays list…</span>
                  </div>
                )}
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Holiday Name</th>
                      <th style={s.thActions}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holidays.map((h) => (
                      <tr key={h.holidayId} style={s.tr}>
                        <td style={s.td}>
                          {new Date(h.holidayDate).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            timeZone: "UTC"
                          })}
                        </td>
                        <td style={s.tdBold}>{h.holidayName}</td>
                        <td style={s.tdActions}>
                          <button
                            className="ado-delete-btn"
                            onClick={() => handleDelete(h.holidayId)}
                            title="Delete Holiday"
                          >
                            <Trash size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    padding: "clamp(12px, 4vw, 24px)",
  },
  header: { marginBottom: "20px" },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
  },
  sub: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: THEME,
    cursor: "pointer",
    fontWeight: "600",
    background: "none",
    border: "none",
    padding: 0,
    marginBottom: "20px",
    outline: "none",
  },
  gridContainer: {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
    width: "100%",
    boxSizing: "border-box",
  },
  formColumn: {
    flex: "1 1 300px",
    maxWidth: "100%",
  },
  tableColumn: {
    flex: "2 1 600px",
    maxWidth: "100%",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "clamp(16px, 4vw, 24px)",
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    boxSizing: "border-box",
    height: "100%",
  },
  cardTitle: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#0f172a",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  input: {
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    background: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    color: "#0f172a",
    height: "42px",
  },
  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    background: THEME,
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "600",
    fontFamily: "inherit",
    outline: "none",
    marginTop: "8px",
    height: "42px",
  },
  tableHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "10px",
  },
  yearSelector: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  refreshBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#64748b",
    cursor: "pointer",
    outline: "none",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  th: {
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
  },
  thActions: {
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
    textAlign: "center",
    width: "80px",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.15s ease",
    ":hover": {
      background: "#f8fafc"
    }
  },
  td: {
    padding: "12px",
    fontSize: "14px",
    color: "#64748b",
  },
  tdBold: {
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
  },
  tdEmail: {
    padding: "12px",
    fontSize: "13px",
    color: "#64748b",
  },
  tdActions: {
    padding: "12px",
    textAlign: "center",
  },
  deleteBtn: {
    background: "none",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    padding: "6px",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s",
    outline: "none",
    ":hover": {
      color: "#ef4444",
      background: "#fee2e2"
    }
  },
  skeletonBar: {
    height: "14px",
    background: "#e2e8f0",
    borderRadius: "4px",
    width: "100px",
    animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  overlayLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(2px)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: THEME,
    fontWeight: "600",
    fontSize: "13px",
    borderRadius: "8px",
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "40px",
    color: "#64748b",
    fontSize: "14px",
  },
  loader: {
    width: "20px",
    height: "20px",
    border: "2.5px solid #dbeafe",
    borderTop: "2.5px solid #2563eb",
    borderRadius: "50%",
    boxSizing: "border-box",
  },
  emptyBox: {
    padding: "40px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "14px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px dashed #e2e8f0",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px 14px",
    borderRadius: "10px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  successBox: {
    background: "#dcfce7",
    color: "#166534",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #86efac",
    fontSize: "13px",
    marginBottom: "16px",
  },
};

/* =====================================================
   SEARCHABLE SELECT / TRIGGER SHARED PANEL STYLES
===================================================== */
const ss = {
  trigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "6px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s, box-shadow 0.15s",
    height: "36px", // compact trigger height to match header rows!
    boxSizing: "border-box",
    gap: "8px",
  },
  triggerText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
  },
  chevron: {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: "#64748b",
    transition: "transform 0.2s ease",
  },
  panel: {
    position: "absolute",
    zIndex: 9999,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(15,23,42,0.1), 0 2px 8px rgba(15,23,42,0.04)",
    overflow: "hidden",
    left: 0,
    right: 0,
    top: "calc(100% + 4px)",
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    background: "#f8fafc",
  },
  searchIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: "13px",
    color: "#0f172a",
    fontFamily: "inherit",
  },
  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    fontSize: "18px",
    lineHeight: 1,
    padding: "0 2px",
    display: "flex",
    alignItems: "center",
  },
  optionList: {
    maxHeight: "220px",
    overflowY: "auto",
    padding: "6px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "9px 10px",
    borderRadius: "7px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "inherit",
    textAlign: "left",
    transition: "background 0.1s",
    boxSizing: "border-box",
  },
  optionLabel: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionSub: {
    fontSize: "11px",
    color: "#94a3b8",
    flexShrink: 0,
  },
  checkIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  emptyMsg: {
    padding: "16px 12px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "13px",
  },
};

/* =====================================================
   CUSTOM DATE PICKER SPECIFIC STYLES
===================================================== */
const dp = {
  trigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s, box-shadow 0.15s",
    height: "42px", // 42px height to match Holiday Name input!
    boxSizing: "border-box",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  navBtn: {
    background: "none",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748b",
    outline: "none",
    transition: "background 0.1s",
  },
  navTitle: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
  },
  weekdaysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    textAlign: "center",
    marginBottom: "4px",
  },
  weekday: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#94a3b8",
    padding: "2px 0",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
    marginBottom: "6px",
  },
  dayCell: {
    aspectRatio: "1",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    outline: "none",
    transition: "background 0.1s, color 0.1s",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid #f1f5f9",
    paddingTop: "8px",
    marginTop: "2px",
  },
  footerBtn: {
    background: "none",
    border: "none",
    fontSize: "11px",
    cursor: "pointer",
    color: "#64748b",
    padding: "4px 6px",
    borderRadius: "4px",
    outline: "none",
  },
};
